import { OpportunityPipelineStatus } from '../types';
import { OPPORTUNITY_STATUS_LABELS } from './commercialPlanning';

export type LinearStage = 'new' | 'qualified' | 'opportunity' | 'bidding' | 'negotiation';
export type OutcomeStatus = 'won' | 'lost' | 'other';

export type DashboardPeriod = {
  key: string;
  label: string;
  start: string;
  end: string;
};

export type KpiValue = {
  value: number | null;
  previous: number | null;
};

export type KpiKey =
  | 'projects_in_progress'
  | 'open_leads'
  | 'open_proposals'
  | 'conversion_rate'
  | 'pipeline_value'
  | 'pipeline_forecast';

export type FunnelStage = {
  status: LinearStage;
  /** Leads on the rail that got at least this far, including those that moved on or closed. */
  reached: number;
  /** Leads sitting in this stage today. */
  count: number;
  value: number;
  forecast: number;
};

export type FunnelOutcome = {
  count: number;
  value: number;
};

export type StageConversion = {
  from: LinearStage;
  to: LinearStage | 'won';
  reached: number;
  advanced: number;
  /** Leads lost or closed as Other with this as their furthest stage. */
  closed_here: number;
  rate: number | null;
};

export type CommercialFunnel = {
  stages: FunnelStage[];
  outcomes: Record<OutcomeStatus, FunnelOutcome>;
  conversions: StageConversion[];
};

export type CommercialResults = {
  won: number;
  lost: number;
  other: number;
  loss_rate: number | null;
};

export type TodoRow = {
  opportunity_id: number;
  title: string | null;
  status: OpportunityPipelineStatus;
  client_name: string | null;
  project_type: string | null;
  next_action_date: string;
  days_until: number;
};

export type ProposalStatus = 'in_progress' | 'on_hold';

export type ProposalRow = {
  project_id: number;
  name: string | null;
  nd_number: string | null;
  client_name: string | null;
  project_type: string | null;
  value: number | null;
  age_days: number | null;
  status: ProposalStatus;
};

export type ActiveProjectStatus = 'in_progress' | 'on_hold' | 'starting';

export type ActiveProjectRow = {
  project_id: number;
  name: string | null;
  nd_number: string | null;
  client_name: string | null;
  project_type: string | null;
  start_date: string | null;
  end_date: string | null;
  status: ActiveProjectStatus;
};

export type ActivityKind =
  | 'lead_created'
  | 'proposal_sent'
  | 'won'
  | 'lost'
  | 'next_action'
  | 'expected_close';

export type ActivityItem = {
  kind: ActivityKind;
  date: string;
  opportunity_id: number;
  title: string | null;
  client_name: string | null;
  project_type: string | null;
  value: number | null;
};

export type FilterOption = {
  id: number;
  name: string;
};

export type CommercialDashboardFilters = {
  period?: string;
  client_id?: number | null;
  project_type?: string | null;
  owner_id?: number | null;
};

export type CommercialDashboard = {
  generated_at: string;
  today: string;
  period: DashboardPeriod;
  previous_period: DashboardPeriod;
  snapshot_date: string;
  periods: DashboardPeriod[];
  filters: {
    client_id: number | null;
    project_type: string | null;
    owner_id: number | null;
  };
  filter_options: {
    clients: FilterOption[];
    owners: FilterOption[];
  };
  kpis: Record<KpiKey, KpiValue>;
  funnel: CommercialFunnel;
  results: CommercialResults;
  todo: {
    rows: TodoRow[];
    total: number;
    overdue: number;
  };
  proposals: {
    rows: ProposalRow[];
    count: number;
    total_value: number;
    avg_age_days: number | null;
  };
  projects: {
    rows: ActiveProjectRow[];
    count: number;
  };
  activities: ActivityItem[];
};

export const LINEAR_STAGES: LinearStage[] = ['new', 'qualified', 'opportunity', 'bidding', 'negotiation'];

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  in_progress: 'In progress',
  on_hold: 'On hold',
};

export const ACTIVE_PROJECT_STATUS_LABELS: Record<ActiveProjectStatus, string> = {
  in_progress: 'In progress',
  on_hold: 'On hold',
  starting: 'Starting',
};

const ACTIVITY_LABELS: Record<ActivityKind, string> = {
  lead_created: 'New lead',
  proposal_sent: 'Proposal sent',
  won: 'Won',
  lost: 'Lost',
  next_action: 'Next action',
  expected_close: 'Expected close',
};

export const activityLabel = (item: ActivityItem, today: string): string =>
  item.kind === 'next_action' && item.date < today ? 'Overdue action' : ACTIVITY_LABELS[item.kind];

export const splitActivities = (items: ActivityItem[], today: string) => ({
  past: items.filter((item) => item.date < today),
  upcoming: items.filter((item) => item.date >= today),
});

export type StageBar = {
  status: LinearStage | 'won';
  label: string;
  value: number;
};

export const buildStageBars = (funnel: CommercialFunnel): StageBar[] => [
  ...funnel.stages.map((stage) => ({
    status: stage.status,
    label: OPPORTUNITY_STATUS_LABELS[stage.status],
    value: stage.value,
  })),
  { status: 'won', label: OPPORTUNITY_STATUS_LABELS.won, value: funnel.outcomes.won.value },
];

export type DeltaMode = 'count' | 'percent' | 'points';

export type KpiDelta = {
  text: string;
  direction: 'up' | 'down' | 'flat';
};

const MINUS = '\u2212';

const signed = (value: number, suffix = ''): KpiDelta => {
  if (value === 0) return { text: `0${suffix}`, direction: 'flat' };
  return value > 0
    ? { text: `+${value}${suffix}`, direction: 'up' }
    : { text: `${MINUS}${Math.abs(value)}${suffix}`, direction: 'down' };
};

export const kpiDelta = (kpi: KpiValue, mode: DeltaMode): KpiDelta | null => {
  const { value, previous } = kpi;
  if (value == null || previous == null) return null;

  if (mode === 'count') return signed(Math.round(value - previous));
  if (mode === 'points') return signed(Math.round((value - previous) * 100), ' pts');

  if (previous === 0) return value === 0 ? signed(0, '%') : null;
  return signed(Math.round(((value - previous) / Math.abs(previous)) * 100), '%');
};

export const dashboardMatchesFilters = (
  dashboard: CommercialDashboard,
  filters: CommercialDashboardFilters,
): boolean =>
  (filters.period == null || filters.period === dashboard.period.key)
  && (filters.client_id ?? null) === dashboard.filters.client_id
  && (filters.project_type ?? null) === dashboard.filters.project_type
  && (filters.owner_id ?? null) === dashboard.filters.owner_id;

export const parseIsoDate = (value: string): Date => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
};

const euroCompact = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const shortDate = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short' });

const fullDate = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' });

const clockTime = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

export const formatEuro = (value: number | null | undefined): string =>
  value == null ? '—' : euroCompact.format(value);

export const formatRate = (rate: number | null | undefined): string =>
  rate == null ? '—' : `${Math.round(rate * 100)}%`;

export const formatShortDate = (value: string | null | undefined): string =>
  value ? shortDate.format(parseIsoDate(value)) : '—';

export const formatDate = (value: string | null | undefined): string =>
  value ? fullDate.format(parseIsoDate(value)) : '—';

export const formatDays = (days: number | null | undefined): string =>
  days == null ? '—' : `${days} d`;

export const formatDueLabel = (daysUntil: number, isoDate: string): string => {
  if (daysUntil < 0) return `${Math.abs(daysUntil)} d overdue`;
  if (daysUntil === 0) return 'Today';
  if (daysUntil === 1) return 'Tomorrow';
  return formatShortDate(isoDate);
};

export const formatUpdatedAt = (generatedAt: string, now: Date = new Date()): string => {
  const stamp = new Date(generatedAt);
  if (Number.isNaN(stamp.getTime())) return '—';
  const sameDay = stamp.toDateString() === now.toDateString();
  const day = sameDay ? 'today' : shortDate.format(stamp);
  return `${day} at ${clockTime.format(stamp)}`;
};
